/**
 * Audio Playback for Realtime API (Web Version)
 * Handles real-time playback of audio chunks from OpenAI Realtime API using Web Audio API.
 * Optimized for browser environments to avoid React Native dependencies.
 */

import { logger } from '@/lib/services/logger';

export interface AudioPlaybackCallbacks {
  onPlaybackStart?: () => void;
  onPlaybackEnd?: () => void;
  onError?: (error: Error) => void;
}

export class RealtimeAudioPlayback {
  private audioQueue: ArrayBuffer[] = [];
  private isPlaying = false;
  private audioContext: AudioContext | null = null;
  private callbacks: AudioPlaybackCallbacks;
  private playbackSource: AudioBufferSourceNode | null = null;
  private isInitialized = false;
  // Track scheduled time to prevent gaps
  private nextStartTime: number = 0;

  constructor(callbacks: AudioPlaybackCallbacks = {}) {
    this.callbacks = callbacks;
  }

  async initialize(): Promise<void> {
    if (this.isInitialized) return;

    try {
      if (typeof window !== 'undefined' && window.AudioContext) {
        this.audioContext = new AudioContext({ sampleRate: 24000 }); // OpenAI uses 24kHz
      } else if (typeof window !== 'undefined' && (window as any).webkitAudioContext) {
        // Safari fallback
        this.audioContext = new (window as any).webkitAudioContext({ sampleRate: 24000 });
      }

      this.isInitialized = true;
      logger.debug('Web Audio playback initialized');
    } catch (error) {
      logger.error('Failed to initialize web audio playback', error as Error);
      throw error;
    }
  }

  async playAudioChunk(audioData: ArrayBuffer): Promise<void> {
    if (!this.isInitialized) {
      await this.initialize();
    }

    if (!this.audioContext) return;

    try {
      const pcm16Data = new Int16Array(audioData);
      const float32Data = new Float32Array(pcm16Data.length);

      for (let i = 0; i < pcm16Data.length; i++) {
        // Convert 16-bit integer to float (-1.0 to 1.0)
        float32Data[i] = pcm16Data[i] / 32768.0;
      }

      const sampleRate = 24000;
      const audioBuffer = this.audioContext.createBuffer(1, float32Data.length, sampleRate);
      audioBuffer.copyToChannel(float32Data, 0);

      const source = this.audioContext.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(this.audioContext.destination);

      // Simple queue management
      // If we are just starting or fallen behind, play immediately (with tiny offset)
      if (this.nextStartTime < this.audioContext.currentTime) {
        this.nextStartTime = this.audioContext.currentTime + 0.05;
      }

      source.start(this.nextStartTime);
      this.nextStartTime += audioBuffer.duration;

      source.onended = () => {
        // Determine if queue is empty based on time
        if (this.audioContext && this.audioContext.currentTime >= this.nextStartTime - 0.1) {
          if (this.isPlaying) {
            this.isPlaying = false;
            this.callbacks.onPlaybackEnd?.();
          }
        }
      };

      this.playbackSource = source;

      if (!this.isPlaying) {
        this.isPlaying = true;
        this.callbacks.onPlaybackStart?.();
      }

      // Resume context if suspended (browser autoplay policy)
      if (this.audioContext.state === 'suspended') {
        await this.audioContext.resume();
      }
    } catch (error) {
      logger.error('Failed to play web audio chunk', error as Error);
      this.callbacks.onError?.(error as Error);
    }
  }

  queueAudioChunk(audioData: ArrayBuffer): void {
    // For Web Audio API, we can just "play" getting the timing logic to schedule it
    // instead of manually array queuing. Detailed scheduling is handled in playAudioChunk.
    // However, if we need to strictly wait for init, we could queue. Use playAudioChunk since it initializes.
    this.playAudioChunk(audioData).catch((err) => {
      logger.error('Failed to queue audio chunk', err as Error);
    });
  }

  async resume(): Promise<void> {
    if (this.audioContext && this.audioContext.state === 'suspended') {
      try {
        await this.audioContext.resume();
        logger.debug('Audio context resumed manually');
      } catch (error) {
        logger.error('Failed to resume audio context', error as Error);
      }
    }
  }

  async stop(): Promise<void> {
    if (this.audioContext) {
      try {
        await this.audioContext.close();
      } catch {}
      this.audioContext = null;
    }
    this.isInitialized = false;
    this.isPlaying = false;
    this.nextStartTime = 0;
    this.callbacks.onPlaybackEnd?.();
  }
}

export function createRealtimeAudioPlayback(
  callbacks?: AudioPlaybackCallbacks
): RealtimeAudioPlayback {
  return new RealtimeAudioPlayback(callbacks);
}
